import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import * as fromRoot from '@app/reducers';
import { select, Store } from '@ngrx/store';
import { Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { AccountActions, TeamActions } from '../actions';
import { RedirectionService } from '../services/redirection.service';
import { TokenService } from '../services/token.service';
import { isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class DomainCreationCanActivateGuard {

  constructor(
    private store$: Store<fromRoot.State>,
    private tokenService: TokenService,
    private redirectionService: RedirectionService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object
  ) { }

  canActivate(): Observable<boolean> {
    if (isPlatformServer(this.platformId)) {
      return of(true);
    }

    return this.isAccountPremium().pipe(mergeMap((accountIsPremium: boolean) => {
      if (accountIsPremium) {
        return this.isSubscriptionValid().pipe(mergeMap((isSubscriptionValid: boolean) => {
          if (isSubscriptionValid) {
            return this.domainNotExists().pipe(map((domainNotCreated: boolean) => {
              return domainNotCreated;
            }));
          } else {
            return of(false);
          }
        }));
      } else {
        return of(false);
      }
    }));
  }

  domainNotExists(): Observable<boolean> {
    this.store$.dispatch(AccountActions.LoadUserRolesAndTeams());
    const domainChecked$ = new ReplaySubject<boolean>();
    this.store$.pipe(
      select(fromRoot.getUserRolesAndTeamsLoaded),
      filter(loaded => !!loaded),
      withLatestFrom(this.store$.select(fromRoot.getUserTeams)),
      takeUntil(domainChecked$),
    ).subscribe(([userAndTeamsLoaded, teams]) => {
      if (userAndTeamsLoaded) {
        if (teams.length > 0) {
          this.router.navigateByUrl('/account/team/' + teams[0].id + '/transfers/sent');
          domainChecked$.next(false);
        } else {
          domainChecked$.next(true);
        }
      }
    });
    return domainChecked$.asObservable();
  }

  isSubscriptionValid(): Observable<boolean> {
    this.store$.dispatch(AccountActions.loadSubscriptionWithPlan());
    const isSubscriptionValid$ = new ReplaySubject<boolean>();
    this.store$.pipe(
      withLatestFrom(
        this.store$.select(fromRoot.getSubscription),
        this.store$.select(fromRoot.getSubscriptionLoaded),
        this.store$.select(fromRoot.getSelectedLanguage),
      ),
      distinctUntilChanged(),
      takeUntil(isSubscriptionValid$),
    ).subscribe(([state, subscription, subscriptionLoaded, language]: any[]) => {
      if (subscriptionLoaded) {
        if (subscription) {
          switch (subscription.status) {
            case Constant.subscriptionStatus.Active:
              isSubscriptionValid$.next(true);
              break;
            case Constant.subscriptionStatus.Pending:
              isSubscriptionValid$.next(true);
              break;
            case Constant.subscriptionStatus.PaymentRequired:
              this.redirectionService.redirectToSubscriptionUpdate();
              isSubscriptionValid$.next(false);
              break;
            case Constant.subscriptionStatus.Inactive:
              this.redirectionService.redirectToPricingPage(language);
              isSubscriptionValid$.next(false);
              break;
            default:
              this.router.navigateByUrl('/account/transfers');
              isSubscriptionValid$.next(false);
              break;
          }
        } else {
          isSubscriptionValid$.next(false);
        }
      }
    });
    return isSubscriptionValid$.asObservable();
  }

  isAccountPremium(): Observable<boolean> {
    const isAccountPremium$ = new ReplaySubject<boolean>();
    this.store$.pipe(
      select(fromRoot.getToken),
      withLatestFrom(
        this.store$.select(fromRoot.getSelectedLanguage),
      ),
      distinctUntilChanged(),
      takeUntil(isAccountPremium$),
    ).subscribe(([token, language]: any[]) => {
      if (token) {
        if (this.tokenService.isAnonymousAccount(token)) {
          this.redirectionService.redirectToPricingPage(language);
          isAccountPremium$.next(false);
        } else {
          if (this.tokenService.isTokenExpired(token)) {
            this.router.navigateByUrl('/signin');
            isAccountPremium$.next(false);
          } else {
            isAccountPremium$.next(true);
          }
        }
      } else {
        this.router.navigateByUrl('/signin');
        isAccountPremium$.next(false);
      }
    });
    return isAccountPremium$.asObservable();
  }

}
